import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { PREFERENCE_KEYS, saveUserPreferences } from '@/store/slices/userPreferences';
import { useLanguageSwitcher } from '@/nova/hooks/useLanguageSwitcher';
import { readManualLanguagePick } from '@/nova/config/manualLanguagePick';
import { toAppLang, toBackendLang } from '@/nova/config/langCodeMap';

import { saveTrackingConsent } from './analytics/consent';
import {
  clearStartPropertiesStash,
  markStartLocaleOverride,
  parseStartProperties,
  readRawStartProperties,
  type StartProperties,
} from './startProperties';

/** True when `code` is one of the tenant's configured UI languages. */
const isSupportedLanguage = (code: string): boolean => (window.config.languages ?? []).includes(code);

/**
 * Consume the `startProperties` hand-off from the external marketing site, once, on
 * configurator entry:
 *  - `consent` → persisted to `localStorage` (browser-scoped) for the tracking scripts wired
 *    up later to gate on.
 *  - `locale` → applied as the UI language AND saved to the cross-device user-preferences API
 *    (via the shared language switcher) so it follows the owner across devices.
 *
 * The `tracking` hand-off (ad-attribution first-touch) is deliberately NOT persisted here — it's
 * saved app-wide + independent of the configurator by `nova/theme/usePersistStartTracking.ts`
 * (mounted in `OwsTheme`), so it lands even for owners who never reach this wizard.
 *
 * The raw param is read from the URL, or — when the configurator's auth gate bounced an
 * unauthenticated arrival through `/login` (which drops the query string) — from the
 * sessionStorage stash the boot shim (`src/dev.js`) saved. The stash is cleared once consumed
 * so a later reload doesn't re-apply a stale hand-off.
 *
 * The prefs save needs an access token; the configurator is auth-gated, so we simply wait for
 * `authenticated` (effectively immediate) before applying the locale.
 */
export function useStartProperties(): void {
  const dispatch = useAppDispatch();
  const { currentLanguage, onChangeLanguage } = useLanguageSwitcher();
  // Gate on a RESOLVED identity (like OwsTheme), not bare token presence: the prefs POST is
  // associated with the user server-side, and the configurator is auth-gated so this is
  // effectively immediate.
  const authenticated = useAppSelector((s) => s.authentication.user.user_id != null);

  // Parse the hand-off exactly once — the URL / stash is stable for the wizard's lifetime.
  const propsRef = useRef<StartProperties | null | undefined>(undefined);
  if (propsRef.current === undefined) propsRef.current = parseStartProperties(readRawStartProperties());

  // Persist consent on entry (no auth gate — it's browser-scoped). The configurator only mounts
  // post-auth, but consent is ALSO readable app-wide before that via getTrackingConsent()'s
  // fallback to the boot stash, so an earlier-page tracker still sees it; writing it here just
  // upgrades that stash to a timestamped record.
  const consentDoneRef = useRef(false);
  useEffect(() => {
    if (consentDoneRef.current) return;
    consentDoneRef.current = true;
    const consent = propsRef.current?.consent;
    if (consent) saveTrackingConsent(consent);
  }, []);

  // Apply + persist the locale once authenticated (so the user-preferences POST lands).
  // `onChangeLanguage` is recreated when auth flips, so read it from a ref to avoid a stale
  // closure without making it a dependency (which would re-run this one-shot effect).
  const onChangeLanguageRef = useRef(onChangeLanguage);
  onChangeLanguageRef.current = onChangeLanguage;
  const localeDoneRef = useRef(false);
  useEffect(() => {
    if (localeDoneRef.current) return;
    // startProperties carries the BACKEND language code (country-code scheme); map it to the app
    // i18n code so the support check, the override flag, and changeLanguage all see the app code.
    const rawLocale = propsRef.current?.locale;
    const locale = rawLocale ? toAppLang(rawLocale) : rawLocale;
    if (!locale || !isSupportedLanguage(locale)) {
      // No startProperties locale hand-off. Fall back to an EXPLICIT in-app language pick made
      // this session (e.g. chosen on /login or /register BEFORE auth, where there was no token to
      // persist it): now that we're authenticated, flush it to the cross-device user-preferences
      // store so the UI choice (the source of truth) overwrites whatever default the freshly
      // minted account carries. Gated on an actual pick, so an existing owner's saved language is
      // never clobbered by a transient boot default.
      const manualPick = readManualLanguagePick();
      if (manualPick && isSupportedLanguage(manualPick)) {
        if (!authenticated) return; // wait for a resolved identity so the prefs save lands
        localeDoneRef.current = true;
        if (manualPick !== currentLanguage) {
          // Diverged (e.g. i18n reset): re-apply AND persist via the switcher.
          onChangeLanguageRef.current(manualPick);
        } else {
          // Already on the picked language: just record it cross-device (overwriting the default).
          dispatch(saveUserPreferences({ [PREFERENCE_KEYS.language]: toBackendLang(manualPick) }));
        }
        clearStartPropertiesStash();
        return;
      }
      localeDoneRef.current = true;
      clearStartPropertiesStash();
      return;
    }
    // Flag the override up-front (before the auth wait) so the app-wide saved-language adoption
    // in OwsTheme never reverts this hand-off locale, even if it runs before we persist to prefs.
    markStartLocaleOverride(locale);
    if (!authenticated) return; // wait for a resolved identity so the prefs save lands
    localeDoneRef.current = true;
    // `onChangeLanguage` switches i18next + the store + the native shell AND (authenticated)
    // saves `language` to the user-preferences API — exactly the "set + persist" we need.
    if (locale !== currentLanguage) {
      onChangeLanguageRef.current(locale);
    } else {
      // Already on the right language: just record the choice cross-device.
      dispatch(saveUserPreferences({ [PREFERENCE_KEYS.language]: toBackendLang(locale) }));
    }
    clearStartPropertiesStash();
  }, [authenticated, currentLanguage, dispatch]);
}

export default useStartProperties;
